import { useEffect, useState } from 'react';
import { Outlet, useLocation, Navigate, useNavigate } from 'react-router';
import { YourCheckaSidebar } from '../components/Sidebar';
import InvestigationFlow from '../components/modals/InvestigationFlow';
import { hasDraft } from '../utils/investigationDraft';
import SupportModal from '../components/modals/SupportModal';
import { useAppSelector } from '../hooks/useAppStore';
import { selectCurrentUser, selectIsAuthenticated } from '../store/slices/authSlice';
import { UserRole } from '../types';

export default function AuthenticatedLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = useAppSelector(selectCurrentUser);
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const [isInvestigationFlowOpen, setIsInvestigationFlowOpen] = useState(false);

    // Someone who started a check before signing up lands here straight after
    // authenticating. Reopen the flow on their parked draft so they finish
    // where they left off instead of hunting for the button again.
    useEffect(() => {
        if (hasDraft()) setIsInvestigationFlowOpen(true);
    }, []);
    const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

    const isAgent = currentUser?.role === UserRole.AGENT;
    const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Show loading state while user profile is being loaded
    if (!currentUser) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#EDEFF3]">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    // Determine active item based on current pathname
    const getActiveItem = (pathname: string) => {
        if (pathname.startsWith('/hr/dashboard')) return 'hr-dashboard';
        if (pathname.startsWith('/hr/earnings')) return 'hr-earnings';
        if (pathname.startsWith('/hr/leaderboard')) return 'hr-leaderboard';
        // Admin medical-labs catalog — checked before the /medical prefix below.
        if (pathname.startsWith('/medical-labs')) return 'medical-labs';
        // Bare landing = dashboard; any deeper path (list or :id detail) = list.
        if (pathname === '/customer-success') return 'cs-dashboard';
        if (pathname.startsWith('/customer-success')) return 'cs-investigations';
        if (pathname === '/medical') return 'medical-dashboard';
        if (pathname.startsWith('/medical')) return 'medical-reports';
        if (pathname.startsWith('/notary')) return 'notary-assignments';
        if (pathname.startsWith('/police')) return 'police-assignments';
        if (pathname.startsWith('/dashboard')) return 'dashboard';
        if (pathname.startsWith('/investigations')) return 'investigations';
        if (pathname.startsWith('/transactions')) return 'transactions';
        if (pathname.startsWith('/earnings')) return 'earnings';
        if (pathname.startsWith('/withdrawals')) return 'withdrawals';
        if (pathname.startsWith('/users')) return 'users';
        if (pathname.startsWith('/pricing')) return 'pricing';
        if (pathname.startsWith('/plans')) return 'plans';
        if (pathname.startsWith('/promo-codes')) return 'promo-codes';
        if (pathname.startsWith('/admin/transactions')) return 'admin-transactions';
        if (pathname.startsWith('/admin/employee-reports')) return 'admin-employee-reports';
        if (pathname.startsWith('/employee-reports')) return 'employee-reports';
        if (pathname.startsWith('/admin/blog')) return 'blog-admin';
        if (pathname.startsWith('/training/admin')) return 'training-admin';
        if (pathname.startsWith('/training')) return 'training';
        if (pathname.startsWith('/admin/messages')) return 'messages';
        if (pathname.startsWith('/settings')) return 'settings';
        if (pathname.startsWith('/admin/support') || pathname.startsWith('/support')) return 'support';
        return 'dashboard'; // default
    };

    const activeItem = getActiveItem(location.pathname);

    const handleRequestInvestigation = () => {
        if (isAgent) {
            // Agents submit reports inline on a specific investigation; route them to the list to pick one.
            navigate('/investigations');
        } else {
            setIsInvestigationFlowOpen(true);
        }
    };

    const handleSupport = () => {
        if (isSuperAdmin) {
            navigate('/admin/support');
        } else {
            setIsSupportModalOpen(true);
        }
    };

    return (
        <div className="flex min-h-screen bg-[#EDEFF3]">
            {/* Sidebar */}
            <YourCheckaSidebar
                activeItem={activeItem}
                onRequestInvestigation={handleRequestInvestigation}
                onSupport={handleSupport}
                userRole={currentUser?.role}
            />

            {/* Main Content */}
            <div className="flex-1 flex flex-col w-full lg:ml-[300px]">
                {/* Page Content */}
                <main className="flex-1 overflow-y-auto w-full pt-14 lg:pt-0">
                    <Outlet context={{ onRequestInvestigation: () => setIsInvestigationFlowOpen(true) }} />
                </main>
            </div>

            {/* Investigation Flow Modals */}
            <InvestigationFlow isOpen={isInvestigationFlowOpen} onClose={() => setIsInvestigationFlowOpen(false)} />

            {/* Support Modal for non-admins */}
            {!isSuperAdmin && <SupportModal isOpen={isSupportModalOpen} onClose={() => setIsSupportModalOpen(false)} />}
        </div>
    );
}
